import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { Marker } from "react-map-gl/dist/maplibre"
import { AirspaceFeaturePanel } from "./AirspaceFeaturePopup"
import {
    ActivePopupState,
    clearActivePopup,
    selectActivePopup,
    selectActiveSearchPanelPlugin,
    selectRecentSearchesPanelOpen,
    selectRightPanelOpen,
} from "../slice"
import { AircraftPopup } from "./AircraftPopup"
import { SimpleBoundingBox, zIndex } from "@nova/constants"
import { useLayoutEffect, useRef, useState, useMemo, useEffect } from "react"
import { useViewport } from "features/map/GlobalViewState"
import { WebMercatorViewport } from "@deck.gl/core"
import { useTick } from "@nova/toolkit-pure-hooks"
import { ProjectPopup } from "./ProjectPopup"
import { ProjectClusterPopup } from "./ProjectClusterPopup"
import { AirspaceFeatureListPopup } from "./AirspaceFeatureListPopup"

const RIGHT_PANEL_WIDTH = 240
const LEFT_PANEL_WIDTH = 384

export const ActivePopup = () => {
    const dispatch = useAppDispatch()
    const activePopup = useAppSelector(selectActivePopup)

    useEffect(() => {
        return () => {
            dispatch(clearActivePopup())
        }
    }, [dispatch])

    const insets = useScreenInsets()

    if (!activePopup) return null

    return (
        <MapPopupPlacement
            key={`${activePopup.type}-${activePopup.id}`}
            popupState={activePopup}
            insets={insets}
        >
            {activePopup.type === "airspace" && (
                <AirspaceFeaturePanel id={activePopup.id} source={activePopup.source} />
            )}
            {activePopup.type === "aircraft" && <AircraftPopup id={activePopup.id} />}
            {activePopup.type === "project" && <ProjectPopup id={activePopup.id} />}
            {activePopup.type === "projectCluster" && <ProjectClusterPopup id={activePopup.id} />}
            {activePopup.type === "airspaceFeatureList" && (
                <AirspaceFeatureListPopup
                    features={activePopup.features}
                    position={activePopup.point!}
                />
            )}
        </MapPopupPlacement>
    )
}

type ScreenInsets = { top?: number; right?: number; bottom?: number; left?: number }
const useScreenInsets = () => {
    const isRightPanelOpen = useAppSelector(selectRightPanelOpen)
    const activePlugin = useAppSelector(selectActiveSearchPanelPlugin)
    const recentSearchesPanelOpen = useAppSelector(selectRecentSearchesPanelOpen)
    return useMemo(
        () => ({
            right: isRightPanelOpen ? RIGHT_PANEL_WIDTH : 0,
            left: !!activePlugin || recentSearchesPanelOpen ? LEFT_PANEL_WIDTH : 0,
        }),
        [activePlugin, isRightPanelOpen, recentSearchesPanelOpen]
    )
}
type MaplibreAnchor = "bottom-left" | "bottom-right" | "top-left" | "top-right"
type Placement = { coord: [number, number]; markerAnchor: MaplibreAnchor }

const computePointBoundingBox = ({
    point,
    dimensions: { width, height },
    viewport,
}: {
    point: { lat: number; lng: number }
    dimensions: { width: number; height: number }
    viewport: WebMercatorViewport
}): SimpleBoundingBox => {
    const [centerX, centerY] = viewport.project([point.lng, point.lat]) as [number, number]
    const pixelMinX = centerX - width / 2
    const pixelMaxX = centerX + width / 2
    // Y increases as you go DOWN the screen - NOT CARTESIAN
    const pixelMinY = centerY - height / 2 // Top edge
    const pixelMaxY = centerY + height / 2 // Bottom edge

    const [minLng, maxLat] = viewport.unproject([pixelMinX, pixelMinY]) as [number, number]
    const [maxLng, minLat] = viewport.unproject([pixelMaxX, pixelMaxY]) as [number, number]
    return { minLat, maxLat, minLng, maxLng }
}

const computePopupPlacement = ({
    popupState,
    viewport,
    panelDimensions,
    insets = { top: 0, right: 0, bottom: 0, left: 0 },
    currentAnchor,
    stickinessRatio = 0.15,
}: {
    popupState: ActivePopupState
    viewport: WebMercatorViewport
    panelDimensions: [number, number]
    insets?: ScreenInsets
    currentAnchor?: MaplibreAnchor
    stickinessRatio?: number
}): Placement | null => {
    if (!popupState) return null
    const computedMargin = (() => {
        const { margin } = popupState
        if (!margin) return { x: 0, y: 0 }
        if (typeof margin === "number") return { x: margin, y: margin }
        return margin
    })()
    const computedBoundingBox: SimpleBoundingBox = (() => {
        switch (popupState.placementType) {
            case "bbox":
                return popupState.bbox
            case "point":
                return computePointBoundingBox({
                    point: popupState.point,
                    dimensions: popupState.renderedDimensions,
                    viewport,
                })
            case "live-point":
                return computePointBoundingBox({
                    point: popupState.getPoint(),
                    dimensions: popupState.renderedDimensions,
                    viewport,
                })
        }
    })()

    const [panelWidth, panelHeight] = panelDimensions

    const screenLeft = insets.left ?? 0
    const screenRight = viewport.width - (insets.right ?? 0)
    const screenTop = insets.top ?? 0
    const screenBottom = viewport.height - (insets.bottom ?? 0)

    const coordTL: [number, number] = [computedBoundingBox.minLng, computedBoundingBox.maxLat]
    const coordTR: [number, number] = [computedBoundingBox.maxLng, computedBoundingBox.maxLat]
    const coordBL: [number, number] = [computedBoundingBox.minLng, computedBoundingBox.minLat]
    const coordBR: [number, number] = [computedBoundingBox.maxLng, computedBoundingBox.minLat]

    const projTL = viewport.project(coordTL) as [number, number]
    const projTR = viewport.project(coordTR) as [number, number]
    const projBL = viewport.project(coordBL) as [number, number]
    const projBR = viewport.project(coordBR) as [number, number]

    const candidates = [
        {
            /*
             * +----------+
             * |          |
             * |   POPUP  |
             * |          |
             * +----------+
             * +----+
             * | BB |
             * +----+
             */
            coord: coordTL,
            anchor: "bottom-left" as MaplibreAnchor,
            basePixel: projTL,
            marginOffset: { x: 0, y: -1 },
            clearanceX: screenRight - projTL[0] - computedMargin.x - panelWidth,
            clearanceY: projTL[1] - screenTop - computedMargin.y - panelHeight,
        },
        {
            /*
             * +----------+
             * |          |
             * |   POPUP  |
             * |          |
             * +----------+
             *       +----+
             *       | BB |
             *       +----+
             */
            coord: coordTR,
            anchor: "bottom-right" as MaplibreAnchor,
            basePixel: projTR,
            marginOffset: { x: 0, y: -1 },
            clearanceX: projTR[0] - screenLeft - computedMargin.x - panelWidth,
            clearanceY: projTR[1] - screenTop - computedMargin.y - panelHeight,
        },
        {
            /*
             * +----+
             * | BB |
             * +----+
             * +----------+
             * |          |
             * |   POPUP  |
             * |          |
             * +----------+
             */
            coord: coordBL,
            anchor: "top-left" as MaplibreAnchor,
            basePixel: projBL,
            marginOffset: { x: 0, y: 1 },
            clearanceX: screenRight - projBL[0] - computedMargin.x - panelWidth,
            clearanceY: screenBottom - projBL[1] - computedMargin.y - panelHeight,
        },
        {
            /*
             *       +----+
             *       | BB |
             *       +----+
             * +----------+
             * |          |
             * |   POPUP  |
             * |          |
             * +----------+
             */
            coord: coordBR,
            anchor: "top-right" as MaplibreAnchor,
            basePixel: projBR,
            marginOffset: { x: 0, y: 1 },
            clearanceX: projBR[0] - screenLeft - computedMargin.x - panelWidth,
            clearanceY: screenBottom - projBR[1] - computedMargin.y - panelHeight,
        },
        {
            /*
             * +----------+ +----+
             * |          | | BB |
             * |   POPUP  | +----+
             * |          |
             * +----------+
             */
            coord: coordTL,
            anchor: "top-right" as MaplibreAnchor,
            basePixel: projTL,
            marginOffset: { x: -1, y: 0 },
            clearanceX: projTL[0] - screenLeft - computedMargin.x - panelWidth,
            clearanceY: screenBottom - projTL[1] - computedMargin.y - panelHeight,
        },
        {
            /*
             * +----+ +----------+
             * | BB | |          |
             * +----+ |   POPUP  |
             *        |          |
             *        +----------+
             */
            coord: coordTR,
            anchor: "top-left" as MaplibreAnchor,
            basePixel: projTR,
            marginOffset: { x: 1, y: 0 },
            clearanceX: screenRight - projTR[0] - computedMargin.x - panelWidth,
            clearanceY: screenBottom - projTR[1] - computedMargin.y - panelHeight,
        },
        {
            /*
             * +----------+
             * |          |
             * |   POPUP  | +----+
             * |          | | BB |
             * +----------+ +----+
             */
            coord: coordBL,
            anchor: "bottom-right" as MaplibreAnchor,
            basePixel: projBL,
            marginOffset: { x: -1, y: 0 },
            clearanceX: projBL[0] - screenLeft - computedMargin.x - panelWidth,
            clearanceY: projBL[1] - screenTop - computedMargin.y - panelHeight,
        },
        {
            /*
             *        +----------+
             *        |          |
             * +----+ |   POPUP  |
             * | BB | |          |
             * +----+ +----------+
             */
            coord: coordBR,
            anchor: "bottom-left" as MaplibreAnchor,
            basePixel: projBR,
            marginOffset: { x: 1, y: 0 },
            clearanceX: screenRight - projBR[0] - computedMargin.x - panelWidth,
            clearanceY: projBR[1] - screenTop - computedMargin.y - panelHeight,
        },
    ]

    const dynamicBuffer = Math.min(viewport.width, viewport.height) * stickinessRatio

    const bestCandidate = candidates.reduce((best, current) => {
        let currentScore = Math.min(current.clearanceX, current.clearanceY)
        let bestScore = Math.min(best.clearanceX, best.clearanceY)
        if (current.anchor === currentAnchor) {
            currentScore += dynamicBuffer
        }
        if (best.anchor === currentAnchor) {
            bestScore += dynamicBuffer
        }
        return currentScore > bestScore ? current : best
    })

    const finalPixel: [number, number] = [
        bestCandidate.basePixel[0] + bestCandidate.marginOffset.x * computedMargin.x,
        bestCandidate.basePixel[1] + bestCandidate.marginOffset.y * computedMargin.y,
    ]

    return {
        coord: viewport.unproject(finalPixel) as [number, number],
        markerAnchor: bestCandidate.anchor,
    }
}

const MapPopupPlacement = ({
    popupState,
    insets,
    children,
}: {
    popupState: NonNullable<ActivePopupState>
    insets?: ScreenInsets
    children: React.ReactNode
}) => {
    const dispatch = useAppDispatch()
    const viewport = useViewport()
    const panelRef = useRef<HTMLDivElement>(null)
    const [panelDimensions, setPanelDimensions] = useState<[number, number] | null>(null)
    const currentAnchorRef = useRef<MaplibreAnchor | undefined>(undefined)
    useTick(popupState.placementType === "live-point" ? popupState.pointRefreshRateMs : undefined)

    useLayoutEffect(() => {
        if (!panelRef.current) return

        const observer = new ResizeObserver((entries) => {
            for (const entry of entries) {
                // Ensure we don't set [0, 0] if it's momentarily hidden
                if (entry.contentRect.width > 0 && entry.contentRect.height > 0) {
                    setPanelDimensions([entry.contentRect.width, entry.contentRect.height])
                }
            }
        })

        observer.observe(panelRef.current)
        return () => observer.disconnect()
    }, [])

    const placement =
        panelDimensions && viewport
            ? computePopupPlacement({
                  popupState,
                  viewport,
                  panelDimensions,
                  insets,
                  currentAnchor: currentAnchorRef.current,
              })
            : null
    useEffect(() => {
        if (placement) {
            currentAnchorRef.current = placement.markerAnchor
        } else if (currentAnchorRef.current) {
            dispatch(clearActivePopup())
            currentAnchorRef.current = undefined
        }
    }, [dispatch, placement])

    return (
        <>
            <div
                ref={panelRef}
                className="fixed"
                style={{ visibility: placement ? "hidden" : "visible", top: -9999 }}
            >
                {children}
            </div>

            {placement && (
                <Marker
                    key={`${popupState.id}-${placement.markerAnchor}`}
                    longitude={placement.coord[0]}
                    latitude={placement.coord[1]}
                    anchor={placement.markerAnchor}
                    style={{ zIndex: zIndex.InspectorPanel }}
                >
                    {children}
                </Marker>
            )}
        </>
    )
}
